import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faHardDrive,
  faCloud,
  faCircleCheck,
  faCircleXmark,
  faExclamationCircle,
  faRotateRight,
  faVial,
  faPen,
  faFolderOpen,
  faBolt,
  faCheck,
  faXmark,
  faMinus,
  faLock,
  faArrowsRotate,
  faSliders,
  faBookOpen,
  faToggleOn,
  faToggleOff,
  faChevronDown,
} from '@fortawesome/free-solid-svg-icons';
import { cn } from '@/lib/cn';
import { ProviderStorageUsageCard } from './components/ProviderStorageUsageCard';
import { toast } from '@/lib/swal';
import Swal from 'sweetalert2';
import { PLATFORM_ROUTES } from '@/routes/platform-routes';
import { SkeletonCard } from '@/components/skeletons';
import { canPlatform } from '@/lib/platform-permissions';
import { usePlatformAuthStore } from '@/stores/platform-auth.store';
import {
  listStorageProviders,
  testLocalProvider,
  updateLocalProvider,
  activateLocalProvider,
  type ProviderResponseDto,
  type LocalTestResult,
  type UpdateLocalProviderInput,
} from './api/platform-storage.api';
import {
  listProviders,
  getProvider,
  updateProviderConfig,
  enableProvider,
  disableProvider,
  activateProvider,
  testProviderDetailed,
  type ProviderListItemDto,
  type ProviderDetailDto,
  type ConfigEntryInput,
  type DetailedTestResult,
} from './api/ame-platform.api';

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatBytes(n: number | null): string {
  if (n === null || n === undefined) return '—';
  if (n === 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(Math.max(n, 1)) / Math.log(1024));
  return `${(n / Math.pow(1024, i)).toFixed(1)} ${units[Math.min(i, 4)]}`;
}

function apiMessage(e: unknown, fallback: string): string {
  const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message;
  return msg ?? fallback;
}

function escapeHtml(value: string): string {
  const map: Record<string, string> = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;',
  };
  return value.replace(/[&<>"']/g, (c) => map[c] ?? c);
}

const HEALTH_CFG = {
  HEALTHY: {
    icon: faCircleCheck,
    color: 'text-green-600 dark:text-green-400',
    bg: 'bg-green-500/10',
    label: 'Healthy',
  },
  DEGRADED: {
    icon: faExclamationCircle,
    color: 'text-amber-500',
    bg: 'bg-amber-500/10',
    label: 'Degraded',
  },
  OFFLINE: {
    icon: faCircleXmark,
    color: 'text-destructive',
    bg: 'bg-destructive/10',
    label: 'Offline',
  },
  UNKNOWN: {
    icon: faExclamationCircle,
    color: 'text-muted-foreground',
    bg: 'bg-muted',
    label: 'Never Tested',
  },
} as const;

function healthCfg(status: string) {
  return HEALTH_CFG[
    (status as keyof typeof HEALTH_CFG) in HEALTH_CFG
      ? (status as keyof typeof HEALTH_CFG)
      : 'UNKNOWN'
  ];
}

const DRIVER_LABEL: Record<string, string> = {
  local: 'Local Filesystem',
  s3: 'Amazon S3',
  azure: 'Azure Blob Storage',
  minio: 'MinIO (S3-compatible)',
  gcs: 'Google Cloud Storage',
  r2: 'Cloudflare R2',
  wasabi: 'Wasabi Hot Storage',
};

/**
 * §5.2 — exactly one status badge per card, every value derived from real
 * fields. No card ever claims "Driver not installed" unless the API's
 * `driverRegistered` actually says so.
 */
type BadgeKind = 'ACTIVE' | 'ENABLED' | 'DISABLED' | 'NOT_CONFIGURED' | 'DRIVER_NOT_INSTALLED';

function statusBadge(
  meta: ProviderListItemDto,
  hasConfig: boolean,
): { kind: BadgeKind; label: string; className: string } {
  if (!meta.driverRegistered) {
    return {
      kind: 'DRIVER_NOT_INSTALLED',
      label: 'DRIVER NOT INSTALLED',
      className: 'bg-muted text-muted-foreground',
    };
  }
  if (meta.isActive) {
    return { kind: 'ACTIVE', label: 'ACTIVE', className: 'bg-primary/10 text-primary' };
  }
  if (meta.isEnabled) {
    return { kind: 'ENABLED', label: 'ENABLED', className: 'bg-foreground/10 text-foreground' };
  }
  if (!hasConfig) {
    return {
      kind: 'NOT_CONFIGURED',
      label: 'NOT CONFIGURED',
      className: 'bg-muted text-muted-foreground',
    };
  }
  return { kind: 'DISABLED', label: 'DISABLED', className: 'bg-muted text-muted-foreground' };
}

/** Required schema keys that have no stored config row — §5.4. */
function missingRequiredKeys(detail: ProviderDetailDto | undefined): string[] {
  if (!detail) return [];
  const present = new Set(detail.config.map((c) => c.key));
  return detail.configSchema.filter((f) => f.required && !present.has(f.key)).map((f) => f.label);
}

// ── Check row (local test results only) ───────────────────────────────────────

function CheckRow({ label, value }: { label: string; value: boolean | null | undefined }) {
  const isNull = value === null || value === undefined;
  return (
    <div className="flex items-center justify-between border-b border-border py-2 last:border-0">
      <span className="text-sm text-foreground">{label}</span>
      {isNull ? (
        <FontAwesomeIcon icon={faMinus} className="h-3.5 w-3.5 text-muted-foreground" />
      ) : (
        <FontAwesomeIcon
          icon={value ? faCheck : faXmark}
          className={cn(
            'h-3.5 w-3.5',
            value ? 'text-green-600 dark:text-green-400' : 'text-destructive',
          )}
        />
      )}
    </div>
  );
}

// ── Schema-driven configuration dialog (SweetAlert2, per UI_UX_STANDARDS) ─────

/**
 * Renders one input per `configSchema` entry — never a hardcoded field list, so
 * Azure and GCS render correctly the moment their drivers ship (§9 rule 1).
 *
 * Returns only the entries the operator actually changed, or null if cancelled.
 *
 * THE SECRET ROUND-TRIP RULE (§5.6): `getConfig` masks every secret to
 * '••••••••'. Submitting that mask back would encrypt the literal bullet string
 * and destroy the real credential silently. Secret inputs therefore start EMPTY
 * and a blank secret is never submitted.
 */
/**
 * Drivers that have a written setup guide.
 *
 * A lookup rather than a boolean so a further guide (Azure, GCS) is a one-line
 * addition and the card/dialog rendering never has to change. The labels are
 * per-driver because the two guides read differently in the two places they are
 * offered — the card sits under a heading that already names the provider, while
 * the dialog needs to name it explicitly.
 */
const PROVIDER_GUIDE: Record<
  string,
  { route: string; cardLabel: string; dialogLabel: string; ariaName: string }
> = {
  s3: {
    route: PLATFORM_ROUTES.STORAGE_PROVIDER_AWS_GUIDE,
    cardLabel: 'Setup Guide',
    dialogLabel: 'AWS S3 Setup Guide',
    ariaName: 'AWS S3',
  },
  r2: {
    route: PLATFORM_ROUTES.STORAGE_PROVIDER_R2_GUIDE,
    cardLabel: 'R2 Setup Guide',
    dialogLabel: 'R2 Setup Guide',
    ariaName: 'Cloudflare R2',
  },
};

async function openConfigDialog(detail: ProviderDetailDto): Promise<ConfigEntryInput[] | null> {
  const schema = detail.configSchema;
  const current = new Map(detail.config.map((c) => [c.key, c]));
  const guide = PROVIDER_GUIDE[detail.driverId];

  // Opens in a NEW TAB so this dialog — and everything already typed into it —
  // stays exactly as it is. `target="_blank"` with `rel="noopener noreferrer"`
  // denies the opened page any `window.opener` handle back into the console.
  // It is an anchor, not a button, so it cannot submit the SweetAlert form.
  const guideLink = guide
    ? `<a href="${escapeHtml(guide.route)}" target="_blank" rel="noopener noreferrer"
           aria-label="Open the ${escapeHtml(guide.ariaName)} setup guide in a new tab"
           title="Opens in a new tab — this form stays open"
           class="mb-4 inline-flex items-center gap-1.5 rounded-lg border border-primary/30 bg-primary/5 px-2.5 py-1.5 text-xs font-medium text-primary hover:bg-primary/10 focus:outline-none focus:ring-2 focus:ring-primary/40">
         <span aria-hidden="true">&#128214;</span> ${escapeHtml(guide.dialogLabel)}
         <span class="text-[10px] opacity-70">(new tab)</span>
       </a>`
    : '';

  const fields = schema
    .map((f) => {
      const existing = current.get(f.key);
      const hasStored = existing !== undefined;
      const value = f.isSecret ? '' : (existing?.value ?? '');
      const id = `sp-cfg-${f.key}`;
      const hint = f.isSecret
        ? hasStored
          ? 'Leave blank to keep the current value'
          : 'Encrypted at rest. Never shown again after saving.'
        : '';
      return `
        <div class="mb-4 text-left">
          <label for="${id}" class="mb-1.5 block text-xs font-medium text-foreground">
            ${escapeHtml(f.label)}${f.required ? ' <span class="text-destructive">*</span>' : ''}
          </label>
          <input id="${id}" type="${f.isSecret ? 'password' : 'text'}"
                 value="${escapeHtml(value)}"
                 placeholder="${escapeHtml(f.placeholder ?? '')}"
                 autocomplete="off" spellcheck="false"
                 ${f.required ? 'aria-required="true"' : ''}
                 class="w-full min-h-[44px] rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30" />
          ${hint ? `<p class="mt-1 text-xs text-muted-foreground">${escapeHtml(hint)}</p>` : ''}
        </div>`;
    })
    .join('');

  const result = await Swal.fire<ConfigEntryInput[]>({
    title: `Configure ${detail.name}`,
    html: `<div class="pt-2">${guideLink}${fields}</div>`,
    width: 560,
    showCancelButton: true,
    confirmButtonText: 'Save',
    cancelButtonText: 'Cancel',
    confirmButtonColor: 'hsl(74 40% 40%)',
    cancelButtonColor: 'hsl(74 15% 45%)',
    reverseButtons: true,
    focusConfirm: false,
    customClass: { popup: 'rounded-xl' },
    preConfirm: () => {
      const out: ConfigEntryInput[] = [];
      for (const f of schema) {
        const el = document.getElementById(`sp-cfg-${f.key}`);
        if (!(el instanceof HTMLInputElement)) continue;
        const raw = el.value;
        const trimmed = raw.trim();
        const existing = current.get(f.key);
        const hasStored = existing !== undefined;

        if (f.isSecret) {
          // Blank secret => keep whatever is stored. Never submit the mask.
          if (trimmed === '') {
            if (f.required && !hasStored) {
              Swal.showValidationMessage(`${f.label} is required`);
              return false;
            }
            continue;
          }
          out.push({ key: f.key, value: raw, isSecret: true });
          continue;
        }

        if (f.required && trimmed === '') {
          Swal.showValidationMessage(`${f.label} is required`);
          return false;
        }
        // Only dirty non-secret fields are submitted.
        if (raw !== (existing?.value ?? '')) {
          out.push({ key: f.key, value: raw, isSecret: false });
        }
      }
      return out;
    },
  });

  if (!result.isConfirmed || !result.value) return null;
  return result.value;
}

// ── Cloud / generic provider card ─────────────────────────────────────────────

interface CloudCardProps {
  meta: ProviderListItemDto;
  detail: ProviderDetailDto | undefined;
  canManage: boolean;
  /** OQ-PR-3 = B — a successful Test in THIS session gates Activate. */
  testedOk: boolean;
  onTested: (id: string, ok: boolean) => void;
  onConfigSaved: (id: string) => void;
  onRefresh: () => void;
}

function CloudProviderCard({
  meta,
  detail,
  canManage,
  testedOk,
  onTested,
  onConfigSaved,
  onRefresh,
}: CloudCardProps) {
  const [busy, setBusy] = useState<null | 'test' | 'save' | 'enable' | 'disable' | 'activate'>(
    null,
  );
  const [testResult, setTestResult] = useState<DetailedTestResult | null>(null);
  const [expanded, setExpanded] = useState(false);

  const hasConfig = (detail?.config.length ?? 0) > 0;
  const badge = statusBadge(meta, hasConfig);
  const hCfg = healthCfg(meta.connectionStatus);
  const missing = missingRequiredKeys(detail);
  const installed = meta.driverRegistered;

  // §6 — visibility derives from real state, so the UI never offers something
  // the server will refuse.
  const showConfigure = installed && canManage && !hasConfig;
  const showEdit = installed && canManage && hasConfig;
  const showTest = installed && hasConfig;
  const showEnable = installed && canManage && !meta.isEnabled && missing.length === 0 && hasConfig;
  const showDisable = installed && canManage && meta.isEnabled && !meta.isActive;
  // OQ-PR-3 = B: configuration alone is NOT enough to activate a cloud provider.
  const showActivate = installed && canManage && meta.isEnabled && !meta.isActive && testedOk;
  const activateBlockedByTest =
    installed && canManage && meta.isEnabled && !meta.isActive && !testedOk;

  const handleConfigure = async () => {
    if (!detail) return;
    const entries = await openConfigDialog(detail);
    if (entries === null) return;
    if (entries.length === 0) {
      toast.info('No changes to save');
      return;
    }
    setBusy('save');
    try {
      await updateProviderConfig(meta.id, entries);
      toast.success('Configuration saved');
      // Configuration changed => any earlier test is stale (§8.2).
      setTestResult(null);
      onConfigSaved(meta.id);
    } catch (e: unknown) {
      toast.error(apiMessage(e, 'Failed to save configuration'));
    } finally {
      setBusy(null);
    }
  };

  const handleTest = async () => {
    setBusy('test');
    setTestResult(null);
    try {
      const r = await testProviderDetailed(meta.id);
      setTestResult(r);
      onTested(meta.id, r.success);
      if (r.success) toast.success(`Connection succeeded · ${r.latencyMs} ms`);
      else toast.error(r.error ?? 'Connection failed');
      onRefresh();
    } catch (e: unknown) {
      onTested(meta.id, false);
      toast.error(apiMessage(e, 'Connection test failed'));
    } finally {
      setBusy(null);
    }
  };

  const handleEnable = async () => {
    setBusy('enable');
    try {
      await enableProvider(meta.id);
      toast.success(`${meta.name} enabled`);
      onRefresh();
    } catch (e: unknown) {
      toast.error(apiMessage(e, 'Failed to enable provider'));
    } finally {
      setBusy(null);
    }
  };

  const handleDisable = async () => {
    const { isConfirmed } = await Swal.fire({
      title: `Disable ${meta.name}?`,
      text: 'New uploads will not use this provider. Existing assets stored on it remain readable while it is enabled — disabling does not delete anything.',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Disable',
      cancelButtonText: 'Cancel',
      confirmButtonColor: 'hsl(74 40% 40%)',
      cancelButtonColor: 'hsl(74 15% 45%)',
      reverseButtons: true,
      customClass: { popup: 'rounded-xl' },
    });
    if (!isConfirmed) return;

    setBusy('disable');
    try {
      await disableProvider(meta.id);
      toast.success(`${meta.name} disabled`);
      onRefresh();
    } catch (e: unknown) {
      toast.error(apiMessage(e, 'Failed to disable provider'));
    } finally {
      setBusy(null);
    }
  };

  const handleActivate = async () => {
    // §6 — the most consequential action on the page. Backdrop dismiss is
    // disabled, and the copy states plainly that NOTHING is migrated.
    const { isConfirmed } = await Swal.fire({
      title: `Activate ${meta.name} as the active storage backend?`,
      html: `
        <div class="text-left text-sm">
          <p class="mb-3"><strong>All new uploads across every tenant will be written to this provider</strong> &mdash; inspection assets and every other upload, including profile photos, branding and QR images.</p>
          <p class="mb-3"><strong>Existing assets are NOT moved.</strong> They remain on the previous provider and stay readable through it for as long as that provider remains <em>enabled</em>.</p>
          <p class="mb-3"><strong>This is not losslessly reversible.</strong> Once files are written here, switching back leaves those files on this provider.</p>
          <p>Only one provider can be active at a time.</p>
        </div>`,
      icon: 'warning',
      width: 560,
      showCancelButton: true,
      confirmButtonText: 'Activate',
      cancelButtonText: 'Cancel',
      confirmButtonColor: 'hsl(25 65% 44%)',
      cancelButtonColor: 'hsl(74 15% 45%)',
      reverseButtons: true,
      allowOutsideClick: false,
      customClass: { popup: 'rounded-xl' },
    });
    if (!isConfirmed) return;

    setBusy('activate');
    try {
      await activateProvider(meta.id);
      toast.success(`${meta.name} is now the active storage backend`);
      onRefresh();
    } catch (e: unknown) {
      toast.error(apiMessage(e, 'Failed to activate provider'));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div
      data-testid={`provider-card-${meta.driverId}`}
      className={cn(
        'rounded-2xl border bg-card',
        meta.isActive ? 'border-primary/40 ring-1 ring-primary/20' : 'border-border',
        !installed && 'opacity-80',
      )}
    >
      <div className="flex items-start gap-4 p-5">
        <div
          className={cn(
            'flex h-12 w-12 shrink-0 items-center justify-center rounded-xl',
            meta.isActive ? 'bg-primary/10' : 'bg-muted',
          )}
        >
          <FontAwesomeIcon
            icon={faCloud}
            className={cn('h-6 w-6', meta.isActive ? 'text-primary' : 'text-muted-foreground')}
          />
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-base font-semibold text-foreground">{meta.name}</span>
            <span
              className={cn(
                'rounded-full px-2.5 py-0.5 text-[10px] font-semibold',
                badge.className,
              )}
            >
              {badge.label}
            </span>
            {!installed && (
              <FontAwesomeIcon
                icon={faLock}
                className="h-3.5 w-3.5 text-muted-foreground"
                title="Driver not implemented"
              />
            )}
          </div>
          <p className="mt-0.5 text-xs text-muted-foreground">
            {DRIVER_LABEL[meta.driverId] ?? meta.driverId} · {meta.driverId} · v{meta.driverVersion}
          </p>
          {meta.lastHealthError && meta.connectionStatus === 'OFFLINE' && (
            <p className="mt-1 text-xs text-destructive">{meta.lastHealthError}</p>
          )}
        </div>

        {installed && (
          <div className={cn('flex shrink-0 items-center gap-1.5 rounded-lg px-3 py-1.5', hCfg.bg)}>
            <FontAwesomeIcon icon={hCfg.icon} className={cn('h-3.5 w-3.5', hCfg.color)} />
            <span className={cn('text-xs font-semibold', hCfg.color)}>{hCfg.label}</span>
          </div>
        )}
      </div>

      {installed && missing.length > 0 && (
        <div className="mx-5 mb-4 rounded-lg border border-amber-400/40 bg-amber-500/5 px-3 py-2">
          <p className="text-xs font-medium text-amber-700 dark:text-amber-400">
            Required configuration missing: {missing.join(', ')}
          </p>
        </div>
      )}

      {activateBlockedByTest && (
        <div className="mx-5 mb-4 rounded-lg border border-border bg-muted/40 px-3 py-2">
          <p className="text-xs text-muted-foreground">
            Run a successful connection test to enable activation.
          </p>
        </div>
      )}

      {/* Actions */}
      <div className="flex flex-wrap items-center gap-2 border-t border-border px-5 py-4">
        {showTest && (
          <button
            onClick={() => void handleTest()}
            disabled={busy !== null}
            aria-disabled={busy !== null}
            className={cn(
              'flex min-h-[44px] items-center gap-1.5 rounded-lg border border-border bg-background px-3 py-2 text-sm font-medium text-foreground transition-colors hover:bg-muted',
              busy !== null && 'cursor-not-allowed opacity-50',
            )}
          >
            <FontAwesomeIcon
              icon={faVial}
              className={cn('h-3.5 w-3.5', busy === 'test' && 'animate-spin')}
            />
            {busy === 'test' ? 'Testing…' : 'Run Connection Test'}
          </button>
        )}

        {/* Setup guide. Rendered for any driver that has one, independently of
            `canManage` — reading documentation is not a privileged action, and an
            operator without manage rights still needs it to prepare the AWS side.
            An anchor, not a button: it opens a NEW TAB so the provider list and
            any open form are left untouched. */}
        {PROVIDER_GUIDE[meta.driverId] && (
          <a
            href={PROVIDER_GUIDE[meta.driverId].route}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`Open the ${DRIVER_LABEL[meta.driverId] ?? meta.driverId} setup guide in a new tab`}
            title="Opens in a new tab — your current screen stays open"
            data-testid={`provider-guide-link-${meta.driverId}`}
            className="flex min-h-[44px] items-center gap-1.5 rounded-lg border border-border bg-background px-3 py-2 text-sm font-medium text-foreground transition-colors hover:bg-muted focus:outline-none focus:ring-2 focus:ring-primary/40"
          >
            <FontAwesomeIcon icon={faBookOpen} className="h-3.5 w-3.5" />
            {PROVIDER_GUIDE[meta.driverId].cardLabel}
          </a>
        )}

        {showConfigure && (
          <button
            onClick={() => void handleConfigure()}
            disabled={busy !== null || !detail}
            aria-disabled={busy !== null || !detail}
            className={cn(
              'flex min-h-[44px] items-center gap-1.5 rounded-lg border border-primary/30 bg-primary/5 px-3 py-2 text-sm font-medium text-primary transition-colors hover:bg-primary/10',
              (busy !== null || !detail) && 'cursor-not-allowed opacity-50',
            )}
          >
            <FontAwesomeIcon icon={faSliders} className="h-3.5 w-3.5" />
            {busy === 'save' ? 'Saving…' : 'Configure'}
          </button>
        )}

        {showEdit && (
          <button
            onClick={() => void handleConfigure()}
            disabled={busy !== null || !detail}
            aria-disabled={busy !== null || !detail}
            className={cn(
              'flex min-h-[44px] items-center gap-1.5 rounded-lg border border-border bg-background px-3 py-2 text-sm font-medium text-foreground transition-colors hover:bg-muted',
              (busy !== null || !detail) && 'cursor-not-allowed opacity-50',
            )}
          >
            <FontAwesomeIcon icon={faPen} className="h-3.5 w-3.5" />
            {busy === 'save' ? 'Saving…' : 'Edit'}
          </button>
        )}

        {showEnable && (
          <button
            onClick={() => void handleEnable()}
            disabled={busy !== null}
            aria-disabled={busy !== null}
            className={cn(
              'flex min-h-[44px] items-center gap-1.5 rounded-lg border border-border bg-background px-3 py-2 text-sm font-medium text-foreground transition-colors hover:bg-muted',
              busy !== null && 'cursor-not-allowed opacity-50',
            )}
          >
            <FontAwesomeIcon icon={faToggleOn} className="h-3.5 w-3.5" />
            {busy === 'enable' ? 'Enabling…' : 'Enable'}
          </button>
        )}

        {showDisable && (
          <button
            onClick={() => void handleDisable()}
            disabled={busy !== null}
            aria-disabled={busy !== null}
            className={cn(
              'flex min-h-[44px] items-center gap-1.5 rounded-lg border border-border bg-background px-3 py-2 text-sm font-medium text-foreground transition-colors hover:bg-muted',
              busy !== null && 'cursor-not-allowed opacity-50',
            )}
          >
            <FontAwesomeIcon icon={faToggleOff} className="h-3.5 w-3.5" />
            {busy === 'disable' ? 'Disabling…' : 'Disable'}
          </button>
        )}

        {showActivate && (
          <button
            onClick={() => void handleActivate()}
            disabled={busy !== null}
            aria-disabled={busy !== null}
            className={cn(
              'flex min-h-[44px] items-center gap-1.5 rounded-lg border border-primary/30 bg-primary/5 px-3 py-2 text-sm font-medium text-primary transition-colors hover:bg-primary/10',
              busy !== null && 'cursor-not-allowed opacity-50',
            )}
          >
            <FontAwesomeIcon icon={faBolt} className="h-3.5 w-3.5" />
            {busy === 'activate' ? 'Activating…' : 'Activate'}
          </button>
        )}

        {installed && (
          <button
            onClick={() => setExpanded((v) => !v)}
            aria-expanded={expanded}
            className="flex min-h-[44px] items-center gap-1.5 rounded-lg border border-border bg-background px-3 py-2 text-sm font-medium text-foreground transition-colors hover:bg-muted"
          >
            Details
            <FontAwesomeIcon
              icon={faChevronDown}
              className={cn('h-3 w-3 transition-transform', expanded && 'rotate-180')}
            />
          </button>
        )}

        {meta.lastHealthCheck && (
          <span className="ml-auto text-xs text-muted-foreground">
            Last check: {new Date(meta.lastHealthCheck).toLocaleString()}
          </span>
        )}
      </div>

      {/* §5.4 — detail panel. Secrets stay masked exactly as the API returned them. */}
      {expanded && detail && (
        <div className="border-t border-border px-5 py-4">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Configuration
          </p>
          {detail.config.length === 0 ? (
            <p className="text-sm text-muted-foreground">Not configured yet.</p>
          ) : (
            <dl className="mb-4 space-y-1">
              {detail.config.map((c) => {
                const label = detail.configSchema.find((f) => f.key === c.key)?.label ?? c.key;
                return (
                  <div
                    key={c.key}
                    className="flex items-baseline justify-between gap-4 border-b border-border py-1.5 last:border-0"
                  >
                    <dt className="text-sm text-muted-foreground">{label}</dt>
                    <dd className="truncate font-mono text-xs text-foreground">{c.value}</dd>
                  </div>
                );
              })}
            </dl>
          )}
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Driver
          </p>
          <dl className="space-y-1">
            <div className="flex items-baseline justify-between gap-4 border-b border-border py-1.5">
              <dt className="text-sm text-muted-foreground">Driver ID</dt>
              <dd className="font-mono text-xs text-foreground">{detail.driverId}</dd>
            </div>
            <div className="flex items-baseline justify-between gap-4 border-b border-border py-1.5">
              <dt className="text-sm text-muted-foreground">Version</dt>
              <dd className="font-mono text-xs text-foreground">{detail.driverVersion}</dd>
            </div>
            <div className="flex items-baseline justify-between gap-4 py-1.5">
              <dt className="text-sm text-muted-foreground">Connection status</dt>
              <dd className="text-xs text-foreground">{detail.connectionStatus}</dd>
            </div>
          </dl>
        </div>
      )}

      {/* §5.5 — cloud drivers NEVER show filesystem checks. A HeadBucket call
          cannot substantiate "write permission", and runDetailedTest synthesises
          those booleans from `success`. Suppressed rather than fabricated. */}
      {testResult && (
        <div
          data-testid={`test-result-${meta.driverId}`}
          className={cn(
            'mx-5 mb-5 rounded-xl border p-4',
            testResult.success
              ? 'border-green-300 bg-green-500/5'
              : 'border-destructive/40 bg-destructive/5',
          )}
        >
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-foreground">Connection Test Result</span>
            <span
              className={cn(
                'text-xs font-semibold',
                testResult.success ? 'text-green-600 dark:text-green-400' : 'text-destructive',
              )}
            >
              {testResult.success ? '✓ Connection succeeded' : '✗ Connection failed'} ·{' '}
              {testResult.latencyMs}ms
            </span>
          </div>
          {testResult.error && <p className="mt-2 text-xs text-destructive">{testResult.error}</p>}
        </div>
      )}

      {/*
        Physical usage for a cloud provider, measured by listing the bucket. This
        is the figure that actually answers "how much is stored here" — the
        catalogue totals elsewhere on this page follow asset OWNERSHIP and move
        when a migration flips it, without a single byte being deleted.
      */}
      <ProviderStorageUsageCard providerId={meta.id} canManage={canManage} />
    </div>
  );
}

// ── Local Storage: Edit Dialog (UNCHANGED — local path-migration flow) ────────

interface EditDialogProps {
  provider: ProviderResponseDto;
  onSaved: (migrationId: string | null) => void;
  onClose: () => void;
}

function EditDialog({ provider, onSaved, onClose }: EditDialogProps) {
  const [form, setForm] = useState<UpdateLocalProviderInput>({
    rootPath: provider.rootPath ?? '',
    publicUrl: provider.publicUrl ?? '',
    maxUploadMb: undefined,
    tempPath: '',
  });
  const [saving, setSaving] = useState(false);

  const set = (key: keyof UpdateLocalProviderInput, value: string | number) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload: UpdateLocalProviderInput = {};
      if (form.rootPath !== undefined && form.rootPath !== (provider.rootPath ?? ''))
        payload.rootPath = form.rootPath;
      if (form.publicUrl !== undefined && form.publicUrl !== (provider.publicUrl ?? ''))
        payload.publicUrl = form.publicUrl;
      if (form.maxUploadMb !== undefined) payload.maxUploadMb = Number(form.maxUploadMb);
      if (form.tempPath !== undefined && form.tempPath !== '') payload.tempPath = form.tempPath;

      if (Object.keys(payload).length === 0) {
        toast.info('No changes to save');
        onClose();
        return;
      }

      const result = await updateLocalProvider(payload);
      if (result.migrationStarted && result.migrationId) {
        await Swal.fire({
          icon: 'info',
          title: 'Migration Started',
          html: `Files are being migrated from<br/><code>${provider.rootPath}</code><br/>to<br/><code>${payload.rootPath}</code><br/><br/>The storage path will switch automatically when migration completes.`,
          confirmButtonText: 'Track Migration',
        });
        onSaved(result.migrationId);
      } else {
        toast.success('Configuration saved');
        onSaved(null);
      }
      onClose();
    } catch (e: unknown) {
      toast.error(apiMessage(e, 'Failed to save configuration'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-lg rounded-2xl border border-border bg-card shadow-xl">
        <div className="flex items-center justify-between border-b border-border p-5">
          <div>
            <h2 className="text-base font-semibold text-foreground">Edit Local Storage</h2>
            <p className="mt-0.5 text-xs text-muted-foreground">
              Changing the Root Path while active will trigger a file migration
            </p>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            className="rounded-lg p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          >
            <FontAwesomeIcon icon={faXmark} className="h-4 w-4" />
          </button>
        </div>

        <div className="space-y-4 p-5">
          <div>
            <label className="mb-1.5 block text-xs font-medium text-foreground">
              Root Path <span className="text-destructive">*</span>
            </label>
            <input
              type="text"
              value={form.rootPath ?? ''}
              onChange={(e) => set('rootPath', e.target.value)}
              placeholder="/app/uploads"
              className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
            />
            {provider.active && form.rootPath !== provider.rootPath && (
              <p className="mt-1 flex items-center gap-1 text-xs text-amber-600 dark:text-amber-400">
                <FontAwesomeIcon icon={faArrowsRotate} className="h-3 w-3" />
                Path change will trigger a file migration
              </p>
            )}
          </div>

          <div>
            <label className="mb-1.5 block text-xs font-medium text-foreground">Public URL</label>
            <input
              type="text"
              value={form.publicUrl ?? ''}
              onChange={(e) => set('publicUrl', e.target.value)}
              placeholder="/uploads"
              className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
            />
          </div>

          <div>
            <label className="mb-1.5 block text-xs font-medium text-foreground">
              Maximum Upload Size (MB)
            </label>
            <input
              type="number"
              value={form.maxUploadMb ?? ''}
              onChange={(e) => set('maxUploadMb', parseInt(e.target.value, 10) || 0)}
              placeholder="100"
              min={1}
              max={10240}
              className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
            />
          </div>

          <div>
            <label className="mb-1.5 block text-xs font-medium text-foreground">
              Temporary Folder
            </label>
            <input
              type="text"
              value={form.tempPath ?? ''}
              onChange={(e) => set('tempPath', e.target.value)}
              placeholder="/tmp/uploads"
              className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
            />
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 border-t border-border p-4">
          <button
            onClick={onClose}
            className="rounded-lg border border-border bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-muted"
          >
            Cancel
          </button>
          <button
            onClick={() => void handleSave()}
            disabled={saving}
            aria-disabled={saving}
            className={cn(
              'rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90',
              saving && 'cursor-not-allowed opacity-50',
            )}
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Local Storage Card (behaviour UNCHANGED; write controls permission-gated) ─

interface LocalCardProps {
  provider: ProviderResponseDto;
  meta: ProviderListItemDto | undefined;
  canManage: boolean;
  onRefresh: () => void;
  onMigrationStarted: (id: string) => void;
}

function LocalStorageCard({
  provider,
  meta,
  canManage,
  onRefresh,
  onMigrationStarted,
}: LocalCardProps) {
  const [testResult, setTestResult] = useState<LocalTestResult | null>(null);
  const [testing, setTesting] = useState(false);
  const [activating, setActivating] = useState(false);
  const [showEdit, setShowEdit] = useState(false);

  const hCfg = healthCfg(provider.health);
  const badge = meta ? statusBadge(meta, true) : null;

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const r = await testLocalProvider();
      setTestResult(r);
      onRefresh();
    } catch {
      toast.error('Connection test failed');
    } finally {
      setTesting(false);
    }
  };

  const handleActivate = async () => {
    const { isConfirmed } = await Swal.fire({
      title: 'Activate Local Storage?',
      text: 'All new uploads will be stored on the local filesystem. Only one provider may be active at a time.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Activate',
    });
    if (!isConfirmed) return;

    setActivating(true);
    try {
      await activateLocalProvider();
      toast.success('Local Storage activated');
      onRefresh();
    } catch (e: unknown) {
      toast.error(apiMessage(e, 'Failed to activate'));
    } finally {
      setActivating(false);
    }
  };

  return (
    <>
      <div
        data-testid="provider-card-local"
        className={cn(
          'rounded-2xl border bg-card',
          provider.active ? 'border-primary/40 ring-1 ring-primary/20' : 'border-border',
        )}
      >
        <div className="flex items-start gap-4 p-5">
          <div
            className={cn(
              'flex h-12 w-12 shrink-0 items-center justify-center rounded-xl',
              provider.active ? 'bg-primary/10' : 'bg-muted',
            )}
          >
            <FontAwesomeIcon
              icon={faHardDrive}
              className={cn('h-6 w-6', provider.active ? 'text-primary' : 'text-muted-foreground')}
            />
          </div>

          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-base font-semibold text-foreground">Local Storage</span>
              {badge && (
                <span
                  className={cn(
                    'rounded-full px-2.5 py-0.5 text-[10px] font-semibold',
                    badge.className,
                  )}
                >
                  {badge.label}
                </span>
              )}
            </div>
            <p className="mt-0.5 text-xs text-muted-foreground">
              {DRIVER_LABEL['local']} · v{provider.driverVersion}
            </p>
          </div>

          <div className={cn('flex shrink-0 items-center gap-1.5 rounded-lg px-3 py-1.5', hCfg.bg)}>
            <FontAwesomeIcon icon={hCfg.icon} className={cn('h-3.5 w-3.5', hCfg.color)} />
            <span className={cn('text-xs font-semibold', hCfg.color)}>{hCfg.label}</span>
          </div>
        </div>

        {/* Stats grid — the page's only KPIs, all from ProviderResponseDto (§5.3) */}
        <div className="grid grid-cols-2 gap-px border-t border-border bg-border sm:grid-cols-4">
          {[
            { label: 'Root Path', value: provider.rootPath ?? '—', mono: true },
            { label: 'Public URL', value: provider.publicUrl ?? '—', mono: true },
            // These two are CATALOGUE figures: they sum `storage_assets` rows whose
            // provider_id is this provider, so they follow OWNERSHIP, not bytes on
            // disk. Labelled as such because a migration flips ownership without
            // deleting anything — see the Storage usage card below for the physical
            // measurement taken from the provider itself.
            { label: 'Catalogued Size', value: formatBytes(provider.totalStorage) },
            { label: 'Catalogued Objects', value: provider.totalObjects.toLocaleString() },
            { label: 'Filesystem', value: formatBytes(provider.filesystemSize) },
            { label: 'Free Space', value: formatBytes(provider.freeSpace) },
            { label: 'Used Space', value: formatBytes(provider.usedSpace) },
            {
              label: 'Usage',
              value:
                provider.filesystemSize && provider.usedSpace
                  ? `${Math.round((provider.usedSpace / provider.filesystemSize) * 100)}%`
                  : '—',
            },
          ].map((stat) => (
            <div key={stat.label} className="bg-card px-4 py-3">
              <p className="text-[11px] font-medium text-muted-foreground">{stat.label}</p>
              <p
                className={cn(
                  'mt-0.5 truncate text-sm font-medium text-foreground',
                  stat.mono && 'font-mono text-xs',
                )}
              >
                {stat.value}
              </p>
            </div>
          ))}
        </div>

        <ProviderStorageUsageCard providerId={provider.id} canManage={canManage} />

        <div className="flex flex-wrap items-center gap-2 border-t border-border px-5 py-4">
          <button
            onClick={() => void handleTest()}
            disabled={testing}
            aria-disabled={testing}
            className={cn(
              'flex min-h-[44px] items-center gap-1.5 rounded-lg border border-border bg-background px-3 py-2 text-sm font-medium text-foreground transition-colors hover:bg-muted',
              testing && 'cursor-not-allowed opacity-50',
            )}
          >
            <FontAwesomeIcon
              icon={faVial}
              className={cn('h-3.5 w-3.5', testing && 'animate-spin')}
            />
            {testing ? 'Testing…' : 'Run Check'}
          </button>

          {/* §3 — write controls are hidden, not disabled, without :manage. */}
          {canManage && (
            <button
              onClick={() => setShowEdit(true)}
              className="flex min-h-[44px] items-center gap-1.5 rounded-lg border border-border bg-background px-3 py-2 text-sm font-medium text-foreground transition-colors hover:bg-muted"
            >
              <FontAwesomeIcon icon={faPen} className="h-3.5 w-3.5" />
              Edit
            </button>
          )}

          <Link
            to={PLATFORM_ROUTES.STORAGE_ASSETS}
            className="flex min-h-[44px] items-center gap-1.5 rounded-lg border border-border bg-background px-3 py-2 text-sm font-medium text-foreground transition-colors hover:bg-muted"
          >
            <FontAwesomeIcon icon={faFolderOpen} className="h-3.5 w-3.5" />
            Browse
          </Link>

          {!provider.active && provider.enabled && canManage && (
            <button
              onClick={() => void handleActivate()}
              disabled={activating}
              aria-disabled={activating}
              className={cn(
                'flex min-h-[44px] items-center gap-1.5 rounded-lg border border-primary/30 bg-primary/5 px-3 py-2 text-sm font-medium text-primary transition-colors hover:bg-primary/10',
                activating && 'cursor-not-allowed opacity-50',
              )}
            >
              <FontAwesomeIcon icon={faBolt} className="h-3.5 w-3.5" />
              {activating ? 'Activating…' : 'Activate'}
            </button>
          )}

          {provider.lastHealthCheck && (
            <span className="ml-auto text-xs text-muted-foreground">
              Last check: {new Date(provider.lastHealthCheck).toLocaleString()}
            </span>
          )}
        </div>

        {/* Local IS a filesystem, so these checks are substantiated here — and
            only here (§5.5). */}
        {testResult && (
          <div
            data-testid="test-result-local"
            className={cn(
              'mx-5 mb-5 rounded-xl border p-4',
              testResult.success
                ? 'border-green-300 bg-green-500/5'
                : 'border-destructive/40 bg-destructive/5',
            )}
          >
            <div className="mb-3 flex items-center justify-between">
              <span className="text-xs font-semibold text-foreground">Connection Test Result</span>
              <span
                className={cn(
                  'text-xs font-semibold',
                  testResult.success ? 'text-green-600 dark:text-green-400' : 'text-destructive',
                )}
              >
                {testResult.success ? '✓ PASSED' : '✗ FAILED'} · {testResult.latencyMs}ms
              </span>
            </div>
            {testResult.error && (
              <p className="mb-3 text-xs text-destructive">{testResult.error}</p>
            )}
            <CheckRow label="Directory exists" value={testResult.checks.directoryExists} />
            <CheckRow label="Write permission" value={testResult.checks.writePermission} />
            <CheckRow label="Read permission" value={testResult.checks.readPermission} />
            <CheckRow label="Delete permission" value={testResult.checks.deletePermission} />
            <div className="flex items-center justify-between py-2">
              <span className="text-sm text-foreground">Available space</span>
              <span className="text-sm font-medium text-foreground">
                {formatBytes(testResult.checks.availableSpaceBytes)}
              </span>
            </div>
            <div className="flex items-center justify-between py-2">
              <span className="text-sm text-foreground">Total space</span>
              <span className="text-sm font-medium text-foreground">
                {formatBytes(testResult.checks.totalSpaceBytes)}
              </span>
            </div>
          </div>
        )}
      </div>

      {showEdit && (
        <EditDialog
          provider={provider}
          onSaved={(migId) => {
            if (migId) onMigrationStarted(migId);
            onRefresh();
          }}
          onClose={() => setShowEdit(false)}
        />
      )}
    </>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export function StorageProvidersPage() {
  const [rich, setRich] = useState<ProviderResponseDto[]>([]);
  const [metas, setMetas] = useState<ProviderListItemDto[]>([]);
  const [details, setDetails] = useState<Record<string, ProviderDetailDto>>({});
  const [loading, setLoading] = useState(true);
  /**
   * A MANUAL refresh, kept separate from `loading` on purpose.
   *
   * `loading` drives the full-page skeleton, which is right for the first load
   * when there is nothing to show yet — but wrong for a refresh, where replacing
   * the populated cards with skeletons throws away context the operator is
   * reading. So a manual refresh drives only the button's own affordances
   * (disabled + spinning icon) and leaves the existing cards on screen.
   *
   * Before this existed, `load()` never set `loading` back to true, so after the
   * first mount the Refresh button produced no visible change at all: it did
   * refetch, but nothing spun, nothing disabled, and unchanged data rendered
   * identically — so the button read as broken.
   */
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [loadError, setLoadError] = useState(false);
  /** OQ-PR-3 = B — successful tests in THIS session, per provider id. */
  const [testedOk, setTestedOk] = useState<Record<string, boolean>>({});

  const role = usePlatformAuthStore((s) => s.user?.role);
  const canManage = canPlatform(role, 'platform:system:manage');

  // Returns the promise so a caller can sequence off it — `handleRefresh` needs
  // to know when the refetch has settled. The chain absorbs its own errors, so
  // the returned promise always resolves.
  const load = () => {
    setLoadError(false);
    return Promise.all([listStorageProviders(), listProviders()])
      .then(async ([richList, metaList]) => {
        const safeRich = Array.isArray(richList) ? richList : [];
        const safeMeta = Array.isArray(metaList) ? metaList : [];
        setRich(safeRich);
        setMetas(safeMeta);

        // Button visibility in §6 depends on whether required config exists,
        // which only the detail endpoint reports — so detail is fetched for
        // every registered non-local provider rather than on expand, to avoid
        // rendering controls that would then have to be retracted.
        const targets = safeMeta.filter((m) => m.driverRegistered && m.driverId !== 'local');
        const loaded = await Promise.all(
          targets.map((m) =>
            getProvider(m.id)
              .then((d) => [m.id, d] as const)
              .catch(() => null),
          ),
        );
        const next: Record<string, ProviderDetailDto> = {};
        for (const entry of loaded) if (entry) next[entry[0]] = entry[1];
        setDetails(next);
      })
      .catch(() => {
        setLoadError(true);
        toast.error('Failed to load providers');
      })
      .finally(() => setLoading(false));
  };

  /**
   * The top-right Refresh button.
   *
   * Deliberately NOT `onClick={load}`: that is what left the control silent.
   * `setLoading` is untouched here, so the skeleton branch stays a first-load
   * concern and the provider cards remain on screen throughout.
   */
  const handleRefresh = () => {
    if (isRefreshing) return;
    setIsRefreshing(true);
    void load().finally(() => setIsRefreshing(false));
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const richById = new Map(rich.map((p) => [p.id, p]));

  // §5.1 — three groups, membership derived from real data.
  const active = metas.filter((m) => m.isActive);
  const available = metas.filter((m) => !m.isActive && m.driverRegistered);
  const unavailable = metas.filter((m) => !m.driverRegistered);

  const renderCard = (meta: ProviderListItemDto) => {
    const richP = richById.get(meta.id);
    if (meta.driverId === 'local' && richP) {
      return (
        <LocalStorageCard
          key={meta.id}
          provider={richP}
          meta={meta}
          canManage={canManage}
          onRefresh={load}
          onMigrationStarted={(id) => {
            void id;
            load();
          }}
        />
      );
    }
    return (
      <CloudProviderCard
        key={meta.id}
        meta={meta}
        detail={details[meta.id]}
        canManage={canManage}
        testedOk={testedOk[meta.id] === true}
        onTested={(id, ok) => setTestedOk((prev) => ({ ...prev, [id]: ok }))}
        onConfigSaved={(id) => {
          // A configuration change invalidates any earlier in-session test.
          setTestedOk((prev) => ({ ...prev, [id]: false }));
          load();
        }}
        onRefresh={load}
      />
    );
  };

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-xl font-semibold text-foreground">Storage Providers</h1>
          <p className="mt-0.5 text-sm text-muted-foreground">
            Manage storage backends — configure, verify, and activate
          </p>
        </div>
        <button
          onClick={handleRefresh}
          disabled={loading || isRefreshing}
          aria-disabled={loading || isRefreshing}
          aria-busy={isRefreshing}
          data-testid="providers-refresh"
          className={cn(
            'flex min-h-[44px] items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-muted',
            (loading || isRefreshing) && 'cursor-not-allowed opacity-50',
          )}
        >
          <FontAwesomeIcon
            icon={faRotateRight}
            className={cn('h-3.5 w-3.5', (loading || isRefreshing) && 'animate-spin')}
          />
          {isRefreshing ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      {loading ? (
        <SkeletonCard count={3} minHeight={140} withStats />
      ) : loadError ? (
        <div role="alert" className="rounded-xl border border-destructive/40 bg-destructive/5 p-5">
          <p className="text-sm font-medium text-foreground">Could not load storage providers.</p>
          <button
            onClick={load}
            className="mt-3 min-h-[44px] rounded-lg border border-border bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-muted"
          >
            Retry
          </button>
        </div>
      ) : metas.length === 0 ? (
        // The request SUCCEEDED and returned no providers. The page cannot know
        // why, so it must not assert a cause. The previous copy blamed seeding
        // and sent operators to platform engineering — while the real defect was
        // a client-side double-unwrap discarding a populated 200 response
        // (PROVIDERS_PAGE_EMPTY_STATE_DIAGNOSIS_20260829.md). State the
        // observation; offer the action that actually helps.
        <div className="rounded-xl border border-border bg-card p-6 text-center">
          <p className="text-sm text-foreground">The storage provider list came back empty.</p>
          <p className="mx-auto mt-1 max-w-prose text-sm text-muted-foreground">
            The request succeeded, so this is unexpected — at least the local provider should always
            be present. Try refreshing; if the list stays empty, capture the response of{' '}
            <code className="rounded bg-muted px-1 py-0.5 font-mono text-xs">
              GET /platform/ame/providers
            </code>{' '}
            before reporting it.
          </p>
          <button
            onClick={load}
            className="mt-4 min-h-[44px] rounded-lg border border-border bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-muted"
          >
            Retry
          </button>
        </div>
      ) : (
        <div className="space-y-6">
          {active.length > 0 && (
            <section>
              <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Active
              </p>
              <div className="space-y-4">{active.map(renderCard)}</div>
            </section>
          )}

          {available.length > 0 && (
            <section>
              <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Available Providers
              </p>
              <div className="space-y-4">{available.map(renderCard)}</div>
            </section>
          )}

          {unavailable.length > 0 && (
            <section>
              <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Unavailable
              </p>
              <div className="space-y-4">{unavailable.map(renderCard)}</div>
            </section>
          )}
        </div>
      )}
    </div>
  );
}
